import React, { useEffect } from 'react';
import { ShippingClient } from 'clients/shipping-client';
import { notifyError } from 'actions/action-notifications';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { PharmacyService } from 'services/utils/pharmacy-service';
import { DeliveryMethodValue } from 'constants/enums';
import { Grid } from '@mui/material';
import PrintShippingButtons from './print-shipping-buttons';
import PickupShippingButtons from './pickup-shipping-buttons';
import ContentsOfPackage from './contents-of-package';
import DeliveryInformation from './delivery-information';
import DeliveryConfirmation from './delivery-confirmation';
import PaymentInformation from './payment-information';
import { IComponentStateItem } from './types';
import { useLabelScan } from './use-shipping-scan';
import { windowFeatureIsEnabled } from '../../config/window-features';

interface IFillDeliveryConfirmationOrderDetailsProps {
  orderId: number;
  statusId: number;
}

export function FillDeliveryConfirmationOrderDetails(
  props: IFillDeliveryConfirmationOrderDetailsProps,
): JSX.Element {
  const { orderId, statusId } = props;

  const [pharmacyService] = React.useState<PharmacyService>(new PharmacyService());
  const [fdcOrderDetails, setFdcOrderDetails] = React.useState<IComponentStateItem>();
  const [pharmacyHasACourierConfigured, setPharmacyHasACourierConfigured] =
    React.useState<boolean>(false);
  const [showButtons, setShowButtons] = React.useState<boolean>(false);
  const [pharmacyInfo, setPharmacyInfo] = React.useState<any>({});
  const status_id = React.useRef(statusId);

  const dispatch = useAppDispatch();

  const scanProps = useLabelScan({ disableScanner: true });
  const getFCData = async () => {
    try {
      const response = await ShippingClient.fetch(
        orderId.toString(),
        'fill-delivery-confirmation-order-details',
      );
      if (response?.data?.error) {
        dispatch(notifyError(response.data?.error));
      } else {
        setFdcOrderDetails(response.data);
        if (windowFeatureIsEnabled('shipping_verification')) {
          await scanProps.fetchHistory(orderId.toString());
          scanProps.setPackageScanRequirements(response.data?.deliveryInformation);
        }
        const currentDispensingPharmacyNpi =
          response.data?.deliveryInformation?.dispensingPharmacyNpi;
        if (currentDispensingPharmacyNpi) {
          const pharmacyResponse = await pharmacyService.getPharmacyByNpi(
            currentDispensingPharmacyNpi,
          );
          setPharmacyInfo(pharmacyResponse || {});
          setShowButtons(pharmacyResponse?.workflow_type === 0);
          setPharmacyHasACourierConfigured(
            !!pharmacyResponse?.lab_logistics_customer_id ||
              !!pharmacyResponse?.spoke_logistics_customer_id ||
              !!pharmacyResponse?.associated_courier_customer_id ||
              !!pharmacyResponse?.nuvizz_courier_customer_id ||
              !!pharmacyResponse?.fetchgoat_customer_id ||
              !!pharmacyResponse?.phox_courier_customer_id,
          );
        }
      }
    } catch (_error) {
      dispatch(notifyError('Failed to load order details.'));
    }
  };

  useEffect(() => {
    const statusIdChanged = status_id.current !== statusId;
    if ((orderId && fdcOrderDetails == null) || statusIdChanged) {
      status_id.current = statusId;
      getFCData();
    }
  }, [orderId, fdcOrderDetails, statusId]);

  const updatePage = () => {
    getFCData();
  };

  const {
    ancillarySupplies,
    deliveryInformation,
    paymentInformation,
    printInformation,
    therapiesNames,
    fdcInformation,
  } = fdcOrderDetails ?? {};

  // Check current delivery methods for UPS, Courier and Internal Delivery
  const isUps = deliveryInformation?.deliveryMethod === DeliveryMethodValue.Ups;
  const isCourier = deliveryInformation?.deliveryMethod === DeliveryMethodValue.Courier;
  const isInternalDelivery =
    deliveryInformation?.deliveryMethod === DeliveryMethodValue.InternalDelivery;
  const isPickup = deliveryInformation?.deliveryMethod === DeliveryMethodValue.PickUp;
  // Determine when to display the print label button based on delivery method and courier services
  const showDeliveryButtons =
    isInternalDelivery || isUps || (isCourier && pharmacyHasACourierConfigured);

  const isCourierWithMissingConfiguration = isCourier && !pharmacyHasACourierConfigured;

  const displayButtons = (): JSX.Element | null => {
    if (isPickup) {
      return (
        <PickupShippingButtons
          currentDeliveryInformation={deliveryInformation}
          currentPrintInformation={printInformation}
          orderId={orderId?.toString()}
          currentFdcInformation={fdcInformation}
          pharmacyInfo={pharmacyInfo}
        />
      );
    }
    if (showDeliveryButtons) {
      return (
        <>
          <PrintShippingButtons
            currentDeliveryInformation={deliveryInformation}
            currentPrintInformation={printInformation}
            currentFdcInformation={fdcInformation}
            statusId={statusId}
            orderId={orderId?.toString()}
            setFdcOrderDetails={setFdcOrderDetails}
            updateShippingPage={updatePage}
            pharmacyInfo={pharmacyInfo}
          />
        </>
      );
    }
    return null;
  };

  return (
    <>
      <DeliveryConfirmation
        currentDeliveryInformation={deliveryInformation}
        currentPrintInformation={printInformation}
      />
      <ContentsOfPackage
        currentMedicationInformation={therapiesNames}
        currentAncillarySupply={ancillarySupplies}
        orderId={orderId}
        scanProps={windowFeatureIsEnabled('shipping_verification') ? scanProps : undefined}
        isPickup={deliveryInformation?.deliveryMethod === DeliveryMethodValue.PickUp}
      />
      <PaymentInformation currentPaymentInformation={paymentInformation} />
      <DeliveryInformation
        currentDeliveryInformation={deliveryInformation}
        currentPrintInformation={printInformation}
        currentFdcInformation={fdcInformation}
        scanProps={windowFeatureIsEnabled('shipping_verification') ? scanProps : undefined}
        isMissingCourierConfiguration={isCourierWithMissingConfiguration}
        workflowType={pharmacyInfo?.workflow_type as number}
      />
      {showButtons ? (
        <Grid container style={{ gap: 20 }} item>
          {displayButtons()}
        </Grid>
      ) : null}
    </>
  );
}
